import { memo } from 'react';

import { parseFontVariant } from '@air/utils-fonts';
import { cn } from '@air/utils-tailwind';

import { DesignPreviewCard } from '~/components/ShareModal/components/designPreview/DesignPreviewCard';
import {
  DesignPreviewCover,
  type DesignPreviewCoverWidth,
} from '~/components/ShareModal/components/designPreview/DesignPreviewCover';
import { DesignPreviewHeaderBar } from '~/components/ShareModal/components/designPreview/DesignPreviewHeaderBar';
import { DesignPreviewSearchField } from '~/components/ShareModal/components/designPreview/DesignPreviewSearchField';
import {
  type DesignPreviewThumbnailRadius,
  DesignPreviewThumbnailStrip,
} from '~/components/ShareModal/components/designPreview/DesignPreviewThumbnailStrip';
import { DesignPreviewTitleRow } from '~/components/ShareModal/components/designPreview/DesignPreviewTitleRow';
import { DesignPreviewWorkspaceIdentity } from '~/components/ShareModal/components/designPreview/DesignPreviewWorkspaceIdentity';
import { useCreateShowcaseDesign } from '~/components/ShowcaseModal/CreateShowcaseDesignProvider';
import { type ShareLinkColorMode } from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';
import { useFontLoader } from '~/hooks/useFontLoader';

export type CreateShowcaseDesignPreviewColorMode = ShareLinkColorMode;
export type CreateShowcaseDesignPreviewCoverWidth = DesignPreviewCoverWidth;
export type CreateShowcaseDesignPreviewPageWidth = 'narrow' | 'standard' | 'wide';
export type CreateShowcaseDesignPreviewThumbnailRadius = DesignPreviewThumbnailRadius;

/**
 * Content-column width per page-width choice. The mini preview card is a fixed width, so this is an
 * approximation — `standard` keeps the original `w-[93%]`, narrow/wide nudge the inner column in/out.
 */
const PAGE_WIDTH_CLASS: Record<CreateShowcaseDesignPreviewPageWidth, string> = {
  narrow: 'w-[80%]',
  standard: 'w-[93%]',
  wide: 'w-full',
};

/**
 * Miniature mocked rendering of the public collection share page, shown inside the share modal's
 * Design tab. Pinned to a fixed 100px card; the cover band, headline font, page theme, page width,
 * and thumbnail corner radius are all reflected live. Foreground colors are derived from the picked
 * backgrounds because the backend stores no foreground colors by design.
 *
 * With an `immersive` cover, the cover sits at the very top of the card and the header bar overlays
 * it with no banner background — the cover bleeds behind the nav, mirroring the public page.
 *
 * Reads every value it renders from {@link useCreateShowcaseDesign} — it must be rendered inside a
 * `CreateShowcaseDesignProvider`. The shared `designPreview/*` building blocks stay prop-driven.
 */
export const CreateShowcaseDesignPreview = memo(() => {
  const {
    pageHex,
    headerHex,
    buttonHex,
    showcaseTitle,
    coverSrc,
    coverWidth,
    logoSrc,
    colorMode,
    coverFade,
    fontFaceUrl,
    fontFamily,
    fontVariant,
    pageWidth,
    thumbnailRadius,
    workspaceLogoSrc,
    workspaceName,
  } = useCreateShowcaseDesign();

  // Setting `font-family` on the title only works if the font is actually in the document — the
  // modal never mounts the public page's loader, so the preview loads its own selected font.
  useFontLoader(fontFamily ? { fontFamily, variant: fontVariant, fontFaceUrl } : undefined);

  // The loader above requests exactly the picked variant's face, so the title must APPLY that same
  // weight/style or the browser synthesizes one — the public page's headline surfaces read the same
  // pair from CSS vars. No variant → undefined, and the title keeps its own `font-bold`.
  const { fontStyle, fontWeight } = parseFontVariant(fontVariant);
  const titleFontVariantStyle = fontFamily && fontVariant ? { fontStyle, fontWeight } : undefined;

  // A stored 'toggle' (wire union member with no authoring option) previews as light because the
  // preview must pick a concrete theme; the public page instead leaves the visitor's preference alone.
  const isDark = colorMode === 'dark';
  // Immersive only changes the layout when there is a cover to bleed behind the header.
  const isImmersive = coverWidth === 'immersive' && !!coverSrc;

  const headerContent = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <DesignPreviewWorkspaceIdentity
          customLogoSrc={logoSrc}
          // Overlaid on the cover there is no banner to contrast against — derive from the page
          // background so the name tracks the page theme, like the public page's immersive header.
          foregroundColor={isImmersive ? getContrastColor(pageHex) : getContrastColor(headerHex)}
          workspaceLogoSrc={workspaceLogoSrc}
          workspaceName={workspaceName}
        />
      </div>
      <DesignPreviewSearchField isDark={isDark} isTranslucent={isImmersive} />
    </>
  );

  const coverBand = coverSrc ? (
    <DesignPreviewCover
      backgroundColor={pageHex}
      coverFade={coverFade}
      coverImageSrc={coverSrc}
      coverWidth={coverWidth}
    />
  ) : null;

  return (
    <DesignPreviewCard backgroundColor={pageHex} className="h-[100px]" data-testid="SHARE_DESIGN_TAB_PREVIEW">
      {isImmersive ? (
        <div className="relative shrink-0">
          {coverBand}
          {/* The header bar overlays the cover with no banner background — matches DesignPreviewHeaderBar's layout. */}
          <div
            className="absolute inset-x-0 top-0 z-10 flex min-h-[31px] w-full items-center gap-2 px-4 py-2"
            data-testid="SHARE_DESIGN_TAB_PREVIEW_HEADER"
          >
            {headerContent}
          </div>
        </div>
      ) : (
        <>
          <DesignPreviewHeaderBar bannerColor={headerHex}>{headerContent}</DesignPreviewHeaderBar>
          {coverBand}
        </>
      )}

      {/* Page-width is approximated via the inner column width — the card itself is fixed-width. */}
      <div
        className={cn(
          'mx-auto flex min-h-0 flex-1 flex-col px-3 pt-2 pb-2',
          PAGE_WIDTH_CLASS[pageWidth],
          isDark ? 'bg-gray-12' : 'bg-gray-1',
        )}
      >
        <DesignPreviewTitleRow
          buttonColor={buttonHex}
          fontFamily={fontFamily}
          title={showcaseTitle}
          {...titleFontVariantStyle}
        />
        <DesignPreviewThumbnailStrip fill isDark={isDark} thumbnailRadius={thumbnailRadius} />
      </div>
    </DesignPreviewCard>
  );
});

CreateShowcaseDesignPreview.displayName = 'CreateShowcaseDesignPreview';
